import { MAX_ZNAKOW_OPISU, MAX_ZNAKOW_TEKSTU } from "@shared/ocenaTypy.js";
import {
  KOMUNIKATY_BLEDOW_PARSERA,
  parsujTekst,
  type KodUwagiParsera,
  type WynikParsowania,
} from "@shared/parserTekstu.js";
import { ApiError } from "./api.js";
import {
  formatujCzasTrwania,
  formatujLimitCzasu,
  formatujPareRozmiarow,
  formatujPozostalyCzas,
  formatujPredkosc,
  formatujRozmiar,
  formatujRozmiaryDoPorownania,
} from "./formatNagrania.js";
import { odmien } from "./polski.js";
import {
  BladPolaczenia,
  BrakPotwierdzeniaPrzyjecia,
  type LimityWgrywania,
  type PostepWgrywania,
  type WynikLimitow,
} from "./wgrywanie.js";

/**
 * Logika formularza "Nowa analiza" bez Reacta i bez DOM: tryby wejscia, podglad tego, co parser
 * rozpoznal w tekscie, walidacja pol i tlumaczenie kodow bledow serwera na zdania.
 *
 * Wszystko tu jest czyste i testowane wprost (tests/dom/formularzWejscia.test.ts). Widok tylko
 * podaje stan i rysuje wynik.
 */

export type TrybWejscia = "nagranie" | "plik" | "tekst";

export const TRYBY_WEJSCIA: readonly { wartosc: TrybWejscia; etykieta: string }[] = [
  { wartosc: "nagranie", etykieta: "Nagranie" },
  { wartosc: "plik", etykieta: "Plik tekstowy" },
  { wartosc: "tekst", etykieta: "Wklejony tekst" },
];

/**
 * Rodzaje materialu w widoku zwyklego uzytkownika (konto `user`): od najczestszego, innymi slowami niz u administratora.
 * Wartosci z tej samej puli, wiec reszta logiki formularza (walidacja, podglad, wysylka) jest wspolna.
 *
 * BEZ NAGRANIA: konto uzytkownika nie wgrywa nagran, dopoki nie ma ekranu sprawdzenia zaslepienia nagrania (imiona
 * powiedziane na glos szlyby do transkrypcji). Serwer i tak odmawia (403 nagrania_wylaczone_dla_konta, docs/konta.md),
 * a ta lista jest jedynym przelacznikiem widoku: formularz nie wysyla trybu, ktorego na niej nie ma.
 */
export const TRYBY_WEJSCIA_UZYTKOWNIKA: readonly { wartosc: TrybWejscia; etykieta: string }[] = [
  { wartosc: "tekst", etykieta: "Wklej tekst" },
  { wartosc: "plik", etykieta: "Plik z tekstem" },
];

export function czyTrybTekstowy(tryb: TrybWejscia): boolean {
  return tryb !== "nagranie";
}

/**
 * Baner o zamianie nazwisk na litery, w obu widokach i przy kazdym rodzaju materialu. Nazwiska i nazwy firm to dane
 * osob trzecich: prosimy o ich zamiane, zanim material wyjedzie. Nagrania nie da sie poprawic po fakcie, wiec zdanie
 * mowi, co z niego trafi do transkrypcji, i prosi o litery albo role w scenkach.
 */
export function tekstBaneruNazwisk(tryb: TrybWejscia): string {
  switch (tryb) {
    case "nagranie":
      return "Zanim wgrasz nagranie: nazwiska i nazwy firm powiedziane na głos trafią do transkrypcji. W scenkach mówcie zamiast nich A, B, C albo role.";
    case "plik":
      return "Zanim wgrasz plik: zamień w nim nazwiska i nazwy firm na A, B, C. Wynik będzie tak samo dobry, a Ty nie wysyłasz cudzych danych.";
    case "tekst":
      return "Zanim wkleisz tekst: zamień nazwiska i nazwy firm na A, B, C. Wynik będzie tak samo dobry, a Ty nie wysyłasz cudzych danych.";
  }
}

// ---------------------------------------------------------------------------
// Odmiana i podsumowanie podgladu
// ---------------------------------------------------------------------------

/** "Rozpoznano 2 mówców, 14 wypowiedzi." (dopelniacz po "rozpoznano" dla mowcow). */
export function podsumowanieRozpoznania(liczbaMowcow: number, liczbaWypowiedzi: number): string {
  const mowcy = `${liczbaMowcow} ${liczbaMowcow === 1 ? "mówcę" : "mówców"}`;
  const wypowiedzi = `${liczbaWypowiedzi} ${odmien(liczbaWypowiedzi, "wypowiedź", "wypowiedzi", "wypowiedzi")}`;
  return `Rozpoznano ${mowcy}, ${wypowiedzi}.`;
}

/** Zdania o tym, co parser zrobil z tekstem niezgodnie z oczekiwaniem. Jedno zdanie na rodzaj uwagi. */
const OPISY_UWAG: Record<KodUwagiParsera, (n: number) => string> = {
  POMINIETY_POCZATEK: () => "Tekst przed pierwszą wypowiedzią (np. nagłówek) został pominięty.",
  POJEDYNCZA_ETYKIETA: (n) =>
    n === 1
      ? "Jedna linia zaczynała się od etykiety, która występuje w tekście tylko raz, więc dokleiliśmy ją do poprzedniej wypowiedzi."
      : `${n} linii zaczynało się od etykiety, która występuje w tekście tylko raz, więc dokleiliśmy je do poprzednich wypowiedzi.`,
  PUSTA_WYPOWIEDZ: (n) =>
    n === 1 ? "Jedna wypowiedź bez treści została pominięta." : `${n} wypowiedzi bez treści zostało pominiętych.`,
  CZASY_CZESCIOWE: () => "Czasy są tylko przy części wypowiedzi, więc ich nie pokazujemy.",
  CZASY_NIEMONOTONICZNE: () => "Czasy w tekście nie rosną po kolei, więc ich nie pokazujemy.",
};

const KOLEJNOSC_UWAG: readonly KodUwagiParsera[] = [
  "POMINIETY_POCZATEK",
  "CZASY_CZESCIOWE",
  "CZASY_NIEMONOTONICZNE",
  "PUSTA_WYPOWIEDZ",
  "POJEDYNCZA_ETYKIETA",
];

// ---------------------------------------------------------------------------
// Podglad tekstu
// ---------------------------------------------------------------------------

export type PodgladTekstu =
  /** Nic jeszcze nie wpisano ani nie wgrano: bez komunikatu, blad pokaze dopiero wysylka. */
  | { rodzaj: "PUSTY" }
  | {
      rodzaj: "PODPISANY";
      liczbaMowcow: number;
      liczbaWypowiedzi: number;
      maCzasy: boolean;
      /** "Rozpoznano 2 mówców, 14 wypowiedzi." */
      podsumowanie: string;
      /** Etykiety mowcow w kolejnosci pierwszego wystapienia. */
      mowcy: string[];
      /** Zdania o rzeczach, ktore parser pominal albo zmienil. */
      uwagi: string[];
    }
  | { rodzaj: "BEZ_PODPISOW"; liczbaSlow: number; podsumowanie: string }
  | { rodzaj: "BLAD"; komunikat: string };

export function podgladZWyniku(wynik: WynikParsowania): PodgladTekstu {
  if (wynik.rodzaj === "BLAD") return { rodzaj: "BLAD", komunikat: wynik.komunikat };

  if (wynik.rodzaj === "BEZ_PODPISOW") {
    // Przy ZA_MALO_LINII_Z_MOWCA podpisy SA w tekscie, tylko jest ich za malo wobec reszty: "nie
    // rozpoznalismy" bylo by nieprawda i uzytkownik nie wiedzialby, co poprawic.
    // Przyklad podpisu to rola, nie imie: przyklad jest wzorem, ktory ludzie kopiuja.
    const wstep =
      wynik.powod === "ZA_MALO_LINII_Z_MOWCA"
        ? "Podpisów mówców (np. Klient: tekst) jest w tym tekście za mało w stosunku do reszty, żeby uznać go za podpisany."
        : "Nie rozpoznaliśmy podpisów mówców (np. Klient: tekst).";
    return {
      rodzaj: "BEZ_PODPISOW",
      liczbaSlow: wynik.liczbaSlow,
      podsumowanie: `${wstep} Model podzieli tekst na wypowiedzi na podstawie opisu rozmowy, a podział zobaczysz i zatwierdzisz przed oceną.`,
    };
  }

  const liczby = new Map<KodUwagiParsera, number>();
  for (const u of wynik.uwagi) liczby.set(u.kod, (liczby.get(u.kod) ?? 0) + 1);
  // Uwagi ponad limit parsera nie maja kodu - dolaczamy je do licznika pojedynczych etykiet,
  // bo to jedyny rodzaj uwagi, ktory moze sie powtarzac setki razy.
  if (wynik.pominietoUwag > 0) {
    liczby.set("POJEDYNCZA_ETYKIETA", (liczby.get("POJEDYNCZA_ETYKIETA") ?? 0) + wynik.pominietoUwag);
  }
  const uwagi = KOLEJNOSC_UWAG.filter((k) => liczby.has(k)).map((k) => OPISY_UWAG[k](liczby.get(k) ?? 0));

  return {
    rodzaj: "PODPISANY",
    liczbaMowcow: wynik.mowcy.length,
    liczbaWypowiedzi: wynik.wypowiedzi.length,
    maCzasy: wynik.maCzasy,
    podsumowanie: podsumowanieRozpoznania(wynik.mowcy.length, wynik.wypowiedzi.length),
    mowcy: wynik.mowcy.map((m) => m.etykieta),
    uwagi,
  };
}

/** Podglad dla tekstu z pola albo z pliku. Pusty tekst to "PUSTY", a nie blad. */
export function podgladTekstu(tekst: string): PodgladTekstu {
  if (tekst.trim().length === 0) return { rodzaj: "PUSTY" };
  return podgladZWyniku(parsujTekst(tekst));
}

/**
 * Zdanie pod polem tekstu w widoku uzytkownika: bez slowa "model" i bez "Rozpoznano ..." w trybie administratora.
 * PUSTY i BLAD nie maja zdania (blad stoi przy polu, F-12). Mowcy: "Rozpoznaliśmy 2 mówców i 357 wypowiedzi."
 */
export function tekstRozpoznaniaDlaUzytkownika(podglad: PodgladTekstu): string | null {
  if (podglad.rodzaj === "PODPISANY") {
    const mowcy = `${podglad.liczbaMowcow} ${podglad.liczbaMowcow === 1 ? "mówcę" : "mówców"}`;
    const wypowiedzi = `${podglad.liczbaWypowiedzi} ${odmien(podglad.liczbaWypowiedzi, "wypowiedź", "wypowiedzi", "wypowiedzi")}`;
    return `Rozpoznaliśmy ${mowcy} i ${wypowiedzi}.`;
  }
  if (podglad.rodzaj === "BEZ_PODPISOW") {
    return "Nie rozpoznaliśmy w tym tekście podpisów mówców (np. Klient: tekst). Podzielimy go na wypowiedzi na podstawie opisu rozmowy, a podział zobaczysz i zatwierdzisz przed oceną.";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Walidacja pol
// ---------------------------------------------------------------------------

export type PoleFormularza = "plik" | "tekst" | "opis" | "zgoda" | "soczewki";

export type BledyFormularza = Partial<Record<PoleFormularza, string>>;

/** Kolejnosc pol na ekranie: od niej zalezy, do ktorego bledu przewijamy. */
export const KOLEJNOSC_POL: readonly PoleFormularza[] = ["plik", "tekst", "opis", "soczewki", "zgoda"];

export interface DaneFormularza {
  tryb: TrybWejscia;
  /** Tryb "nagranie": czy wybrano plik. */
  maPlikAudio: boolean;
  /**
   * Tryb "plik": zawartosc po odczytaniu (null - nie wybrano albo nie odczytano) i ewentualny blad
   * odczytu. Tryb "tekst": tekst z pola w `tekst`.
   */
  tekst: string;
  maPlikTekstu: boolean;
  /** Blad pliku znany juz przy wyborze (za duzy, zle kodowanie, zly typ). */
  bladPliku: string | null;
  opis: string;
  zgoda: boolean;
  wybraneParametry: readonly string[];
}

export const KOMUNIKAT_BRAKU_PARAMETROW = "Zaznacz co najmniej jeden parametr oceny.";
export const KOMUNIKAT_BRAKU_ZGODY = "Zaznacz, że rozmówcy wiedzą o analizie i wyrazili na nią zgodę.";
export const KOMUNIKAT_BRAKU_OPISU =
  "Opisz rozmowę. W tym tekście nie ma podpisów mówców, więc model potrzebuje opisu, żeby je przypisać.";

/** To samo co KOMUNIKAT_BRAKU_OPISU, bez slowa "model": uzytkownik nie wie, kto przypisuje mowcow. */
export const KOMUNIKAT_BRAKU_OPISU_UZYTKOWNIKA =
  "Opisz rozmowę. W tym tekście nie ma podpisów mówców, więc potrzebujemy opisu, żeby przypisać do nich wypowiedzi.";

export function komunikatZaDlugiegoOpisu(dlugosc: number): string {
  return `Opis ma ${dlugosc} znaków, a limit to ${MAX_ZNAKOW_OPISU}. Skróć go.`;
}

/** Wszystkie bledy naraz: uzytkownik ma zobaczyc, co jeszcze trzeba poprawic, nie po jednym. */
export function walidujFormularz(d: DaneFormularza): BledyFormularza {
  const bledy: BledyFormularza = {};

  // Wynik parsera dla tekstu, ktory pojedzie na serwer; null, gdy nie ma jeszcze czego parsowac.
  let podglad: PodgladTekstu | null = null;

  if (d.tryb === "nagranie") {
    if (d.bladPliku) bledy.plik = d.bladPliku;
    else if (!d.maPlikAudio) bledy.plik = "Najpierw wybierz nagranie, które mamy przeanalizować.";
  } else if (d.tryb === "plik") {
    if (d.bladPliku) bledy.plik = d.bladPliku;
    else if (!d.maPlikTekstu) bledy.plik = "Najpierw wybierz plik tekstowy z rozmową.";
    else podglad = podgladZWyniku(parsujTekst(d.tekst));
  } else {
    // Pusty tekst to dla parsera TEKST_PUSTY, czyli ten sam komunikat co z serwera.
    podglad = podgladZWyniku(parsujTekst(d.tekst));
  }

  // Dla pliku blad stoi przy strefie, dla tekstu z pola - przy polu.
  if (podglad?.rodzaj === "BLAD") bledy[d.tryb === "plik" ? "plik" : "tekst"] = podglad.komunikat;

  const opis = d.opis.trim();
  if (opis.length > MAX_ZNAKOW_OPISU) {
    bledy.opis = komunikatZaDlugiegoOpisu(opis.length);
  } else if (podglad?.rodzaj === "BEZ_PODPISOW" && opis.length === 0) {
    bledy.opis = KOMUNIKAT_BRAKU_OPISU;
  }

  if (d.wybraneParametry.length === 0) bledy.soczewki = KOMUNIKAT_BRAKU_PARAMETROW;
  if (!d.zgoda) bledy.zgoda = KOMUNIKAT_BRAKU_ZGODY;

  return bledy;
}

/** Pierwsze pole z bledem w kolejnosci ekranu, albo null. */
export function pierwszeZBledem(bledy: BledyFormularza): PoleFormularza | null {
  return KOLEJNOSC_POL.find((p) => bledy[p] !== undefined) ?? null;
}

// ---------------------------------------------------------------------------
// Nagranie: limity, walidacja na miejscu, postep wysylania
// ---------------------------------------------------------------------------

/**
 * Podpowiedz w strefie wgrywania (UP-1: wymagania widoczne OD RAZU). Limity pochodza z serwera, nie ze stalych w
 * kliencie. Zanim sie wczytaja (albo gdy ich nie da sie wczytac) nie pokazujemy liczby, ktorej nie znamy.
 */
export function tekstWymaganNagrania(wynik: WynikLimitow | null): string {
  const formaty = "WAV, MP3, M4A";
  if (!wynik) return `${formaty} - limity rozmiaru i długości sprawdzi serwer przy wysyłce`;
  const { limity } = wynik;
  const rozmiar = `maks. ${formatujRozmiar(limity.maxBajtowNagrania)}`;
  // Starszy serwer nie zna limitu czasu: milczymy o godzinach zamiast je zmyslac.
  return limity.maxCzasuNagraniaSek === null
    ? `${formaty} - ${rozmiar}`
    : `${formaty} - ${rozmiar}, do ${formatujLimitCzasu(limity.maxCzasuNagraniaSek)}`;
}

/** UP-2: walidacja nagrania NA MIEJSCU, zanim plik pojedzie na serwer. Bez znanych limitow rozmiaru nie sprawdzamy: rozstrzyga serwer. */
export function walidujNagranie(plik: { size: number; type: string }, limity: LimityWgrywania | null): string | null {
  if (limity && plik.size > limity.maxBajtowNagrania) {
    // Plik tuz nad limitem nie moze dostac zdania "3 GB - limit to 3 GB": przy rownych napisach dokladamy miejsca po przecinku.
    const [rozmiar, limit] = formatujRozmiaryDoPorownania(plik.size, limity.maxBajtowNagrania);
    return `Ten plik ma ${rozmiar} - limit to ${limit}. Podziel nagranie na części albo zapisz je w mniejszym formacie, np. MP3.`;
  }
  // Pusty typ MIME dostaje rzadkie rozszerzenie, ktorego przegladarka nie zna.
  // Takiego pliku nie odrzucamy - rozstrzyga serwer, ktory zaglada do srodka.
  if (plik.type !== "" && !/^(audio|video)\//.test(plik.type)) {
    return "To nie jest plik audio ani wideo. Wybierz nagranie rozmowy.";
  }
  return null;
}

/**
 * Margines na niedokladny odczyt czasu z przegladarki (MP3 bez naglowka VBR to szacunek z bitrate'u): odrzucamy na
 * miejscu dopiero nagranie ewidentnie za dlugie, a graniczne zostawiamy serwerowi, ktory mierzy ffprobe.
 */
const TOLERANCJA_ODCZYTU_CZASU = 1.05;

/** Komunikat dla nagrania dluzszego niz limit: ten sam po stronie klienta (odczyt z metadanych) i serwera (413). */
export function komunikatZaDlugiegoNagrania(czasSek: unknown, maxSek: unknown): string {
  const limit = typeof maxSek === "number" && Number.isFinite(maxSek) && maxSek > 0 ? maxSek : null;
  const czas = typeof czasSek === "number" && Number.isFinite(czasSek) && czasSek > 0 ? czasSek : null;
  // Liczby podajemy tylko te, ktore mamy: serwer w odpowiedzi 413 powinien przyslac obie, ale nie zgadujemy brakujacej.
  let zdanie: string;
  if (czas !== null && limit !== null) {
    zdanie = `To nagranie trwa ${formatujCzasTrwania(czas)}, a serwer przyjmuje nagrania do ${formatujLimitCzasu(limit)}.`;
  } else if (limit !== null) {
    zdanie = `Serwer przyjmuje nagrania do ${formatujLimitCzasu(limit)}, a to nagranie jest dłuższe.`;
  } else if (czas !== null) {
    zdanie = `To nagranie trwa ${formatujCzasTrwania(czas)}, czyli więcej, niż serwer przyjmuje.`;
  } else {
    zdanie = "To nagranie jest dłuższe, niż serwer przyjmuje.";
  }
  return `${zdanie} Podziel je na krótsze części i wgraj osobno.`;
}

/** Nagranie ewidentnie dluzsze niz limit serwera, znane z metadanych przegladarki. null: brak powodu do odrzucenia. */
export function walidujCzasNagrania(czasSek: number | null, limity: LimityWgrywania | null): string | null {
  if (czasSek === null || !limity || limity.maxCzasuNagraniaSek === null) return null;
  if (czasSek <= limity.maxCzasuNagraniaSek * TOLERANCJA_ODCZYTU_CZASU) return null;
  return komunikatZaDlugiegoNagrania(czasSek, limity.maxCzasuNagraniaSek);
}

export interface OpisPostepuWysylania {
  /** "co dzieje sie TERAZ" (PR-2); rozmiary "1,2 z 2,4 GB", bez procentu: ten rysuje sam pasek. */
  etykieta: string;
  /** undefined: nie da sie zmierzyc (pasek nieokreslony). */
  procent?: number;
  /** Wiersz pod paskiem: numer porcji, srednia predkosc i czas do konca - te dwie ostatnie tylko, gdy sa wiarygodne. */
  szczegoly: string;
  /** Zdanie o zerwanym polaczeniu i ponowieniu; null, gdy wszystko idzie dobrze. */
  ponowienie: string | null;
  /** Po zlozeniu pliku na serwerze przerwanie nic juz nie daje (analiza moze powstac), wiec przycisku nie pokazujemy. */
  mozeAnulowac: boolean;
}

/** Postep wysylania na zdania do paska. Czysta funkcja: widok tylko to rysuje. */
export function opisPostepuWysylania(p: PostepWgrywania): OpisPostepuWysylania {
  const odliczanie = p.ponowienie ? `za ${p.ponowienie.zaSek} s (próba ${p.ponowienie.proba} z ${p.ponowienie.maxProb})` : "";
  // 409 porcja_w_toku to nie awaria lacza: serwer jeszcze zamyka poprzednie polaczenie, wiec nie straszymy "przerwanym polaczeniem".
  const ponowienie = p.ponowienie
    ? p.ponowienie.przyczyna === "porcja_w_toku"
      ? `Poprzednie połączenie jeszcze się zamyka, ponawiam ${odliczanie}.`
      : `Połączenie z serwerem przerwane. Ponawiam ${odliczanie}.`
    : null;

  switch (p.faza) {
    case "przygotowanie":
      return { etykieta: "Przygotowuję wysyłanie nagrania...", szczegoly: "", ponowienie, mozeAnulowac: true };
    case "jedno_zadanie":
      // Jedno zadanie HTTP nie raportuje postepu - pasek jest nieokreslony zamiast zmyslonych procentow.
      return {
        etykieta: `Wysyłanie nagrania (${formatujRozmiar(p.razemBajtow)})...`,
        szczegoly: "",
        ponowienie,
        mozeAnulowac: true,
      };
    case "skladanie":
      return {
        etykieta: "Serwer składa i sprawdza nagranie...",
        szczegoly: "Przy długim nagraniu to chwilę trwa. Nie zamykaj tej strony.",
        ponowienie,
        mozeAnulowac: false,
      };
    case "porcje": {
      const czesci = [`Część ${p.porcja} z ${p.liczbaPorcji}`];
      if (p.predkoscBajtowNaSek !== null) czesci.push(formatujPredkosc(p.predkoscBajtowNaSek));
      if (p.pozostaloSek !== null) czesci.push(`jeszcze ${formatujPozostalyCzas(p.pozostaloSek)}`);
      return {
        etykieta: `Wysyłanie nagrania: ${formatujPareRozmiarow(p.wyslaneBajty, p.razemBajtow)}`,
        // Podloga, nie zaokraglenie: 100% oznacza "wszystko u serwera", a nie "prawie".
        procent: p.razemBajtow > 0 ? Math.floor((p.wyslaneBajty / p.razemBajtow) * 100) : 0,
        szczegoly: czesci.join(" - "),
        ponowienie,
        mozeAnulowac: true,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Bledy serwera
// ---------------------------------------------------------------------------

export interface BladDoPokazania {
  /** Pole, przy ktorym stoi komunikat. "ogolny" - przy przycisku wysylki. */
  pole: PoleFormularza | "ogolny";
  tekst: string;
  /** Wybor parametrow jest nieaktualny: trzeba pobrac liste jeszcze raz. */
  odswiezParametry?: boolean;
  /** Analiza mogla powstac mimo bledu (brak potwierdzenia): lista analiz ma sie odswiezyc, zeby to bylo widac. */
  odswiezListeAnaliz?: boolean;
}

function napis(wartosc: unknown): string | null {
  return typeof wartosc === "string" && wartosc.trim().length > 0 ? wartosc : null;
}

function liczba(wartosc: unknown, domyslna: number): number {
  return typeof wartosc === "number" && Number.isFinite(wartosc) ? wartosc : domyslna;
}

function formatujLiczbe(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/**
 * Zdanie ogolne dla uzytkownika, gdy nie ma lepszego: bez kodu w nawiasie, bez slow "serwer", "model" i "dysk". Zamiast
 * kodu nieznanego bledu jest prosba o ponowienie albo o znak do nas.
 */
export const BLAD_OGOLNY_WGRANIA_UZYTKOWNIKA = "Nie udało się wgrać rozmowy. Spróbuj ponownie za chwilę albo daj nam znać.";

/**
 * 403 nagrania_wylaczone_dla_konta: konto uzytkownika nie wgrywa nagran (docs/konta.md). Widok uzytkownika nie ma juz
 * tego trybu, wiec odmowe zobaczy glownie karta otwarta przed wdrozeniem; zdanie mowi, co zrobic, bez kodu i bez "serwer".
 */
export const KOMUNIKAT_NAGRANIA_WYLACZONE =
  "Na tym koncie oceniamy na razie tylko rozmowy w formie tekstu. Wklej tekst rozmowy albo wybierz plik z tekstem.";

/**
 * Bledy STANU SERWERA (brak miejsca, zajete wgrywanie, uszkodzona porcja, brak modelu) w wariancie uzytkownika. Konto `user`
 * nigdy nie widzi tresci bledow technicznych, liczb o dysku, kodow w nawiasach ani slowa "model": te przypadki dostaja
 * zdanie o tym, co zrobic. null: kod nie jest stanem serwera (obsluguje go wspolny przelacznik).
 */
function bladStanuSerweraDlaUzytkownika(kod: string): BladDoPokazania | null {
  switch (kod) {
    case "brak_miejsca":
      return { pole: "ogolny", tekst: "Nie możemy teraz przyjąć tej rozmowy. Spróbuj później albo daj nam znać." };
    case "za_duzo_wgrywan":
      return { pole: "ogolny", tekst: "Przyjmujemy teraz najwięcej rozmów naraz. Poczekaj, aż któraś się skończy, i spróbuj ponownie." };
    case "niekompletny_plik":
      return { pole: "ogolny", tekst: "Rozmowa dotarła do nas tylko w części. Wyślij ją jeszcze raz." };
    case "wgrywanie_juz_przetworzone_albo_przerwane":
      return { pole: "ogolny", tekst: "To wysyłanie zostało już zakończone albo przerwane. Wyślij rozmowę jeszcze raz." };
    case "porcja_w_toku":
      return { pole: "ogolny", tekst: "Poprzednia część nagrania jest jeszcze przetwarzana. Spróbuj ponownie za chwilę." };
    case "nie_ma_takiego_wgrywania":
      return { pole: "ogolny", tekst: "Nie pamiętamy już tego wysyłania, bo przerwa była zbyt długa. Wyślij rozmowę jeszcze raz." };
    case "zla_dlugosc_porcji":
    case "zly_numer_porcji":
    case "niezgodna_odpowiedz_serwera":
      return {
        pole: "ogolny",
        tekst: "Część nagrania dotarła do nas w uszkodzonej postaci. Wyślij rozmowę jeszcze raz, a jeśli się powtórzy, daj nam znać.",
      };
    case "model_niedostepny":
      return { pole: "ogolny", tekst: "Nie możemy teraz ocenić tej rozmowy. Spróbuj ponownie później albo daj nam znać." };
    default:
      return null;
  }
}

/**
 * Kod bledu z serwera na zdanie przy wlasciwym polu (F-12, S-1: po ludzku, nigdy sam kod).
 * Nieznany kod nie znika: trafia do komunikatu ogolnego, zeby dalo sie go zglosic. W wariancie uzytkownika nieznany kod
 * i stany serwera dostaja zdanie bez kodu (patrz `bladStanuSerweraDlaUzytkownika`).
 */
export function bladSerweraNaPole(
  err: unknown,
  tryb: TrybWejscia,
  limitMb?: number,
  czyAdmin = true,
  /** Widok zwyklego uzytkownika: przycisk nazywa sie "Oceń rozmowę", lista to "Moje rozmowy", a o oknie modeli nie ma mowy. */
  wariant: "administrator" | "uzytkownik" = "administrator",
): BladDoPokazania {
  const uzytkownik = wariant === "uzytkownik";
  if (err instanceof BladPolaczenia) {
    // Wgrywanie zostaje na serwerze: kolejne "Analizuj" z tym samym plikiem wznawia od miejsca, do ktorego dotarlo.
    // Liczby tylko te, ktore podal serwer; bez nich zdanie jest bez liczb.
    const postep =
      err.wyslaneBajty !== null && err.razemBajtow !== null
        ? ` po ${formatujPareRozmiarow(err.wyslaneBajty, err.razemBajtow)}`
        : "";
    return {
      pole: "ogolny",
      tekst: `Wysyłanie przerwane${postep}. Kliknij ${uzytkownik ? "Oceń rozmowę" : "Analizuj"}, aby kontynuować od tego miejsca.`,
    };
  }
  if (err instanceof BrakPotwierdzeniaPrzyjecia) {
    return {
      pole: "ogolny",
      tekst:
        uzytkownik
          ? "Całe nagranie dotarło do nas, ale nie dostaliśmy potwierdzenia, że ocena ruszyła. Sprawdź listę w zakładce Moje rozmowy, zanim wyślesz nagranie jeszcze raz - mogła już powstać."
          : "Całe nagranie dotarło na serwer, ale nie dostaliśmy potwierdzenia, że analiza ruszyła. Sprawdź listę analiz poniżej, zanim wyślesz nagranie jeszcze raz - mogła już powstać.",
      odswiezListeAnaliz: true,
    };
  }
  if (!(err instanceof ApiError)) {
    return { pole: "ogolny", tekst: "Nie udało się wgrać. Sprawdź połączenie i spróbuj ponownie." };
  }

  const s = err.szczegoly;
  // Sesja wygasla w trakcie dlugiego wysylania (godziny): kod bledu nic by tu nie powiedzial.
  if (err.status === 401) {
    const co = tryb === "nagranie" ? "nagranie" : "rozmowę";
    return { pole: "ogolny", tekst: `Sesja wygasła. Zaloguj się ponownie i wyślij ${co} jeszcze raz.` };
  }
  if (uzytkownik) {
    const stanSerwera = bladStanuSerweraDlaUzytkownika(err.code);
    if (stanSerwera !== null) return stanSerwera;
  }
  switch (err.code) {
    case "plik_za_duzy": {
      // Serwer z wgrywaniem porcjami podaje tez bajty: wtedy limit w GB, a nie w zaokraglonych MB.
      const limit =
        typeof s.maxBajtow === "number" && Number.isFinite(s.maxBajtow)
          ? formatujRozmiar(s.maxBajtow)
          : typeof s.maxMb === "number" || limitMb !== undefined
            ? `${liczba(s.maxMb, limitMb ?? 0)} MB`
            : null;
      const zdanie = limit === null ? "Plik jest za duży dla serwera." : `Plik jest za duży - serwer przyjmuje najwyżej ${limit}.`;
      return { pole: "plik", tekst: `${zdanie} Podziel nagranie na części albo zapisz je w mniejszym formacie, np. MP3.` };
    }
    case "nagranie_za_dlugie":
      return { pole: "plik", tekst: komunikatZaDlugiegoNagrania(s.durationSec, s.maxSek) };
    case "plik_za_duzy_dla_dostawcy": {
      if (!czyAdmin) {
        // Konto bez roli administratora nie wybiera modelu i nie ma okna "Zmien modele", a serwer przestaje mu podawac
        // nazwe dostawcy. Zdanie o rozmiarze pliku, bez dostawcy i bez rady, ktorej nie moze wykonac.
        const limit = typeof s.maxMb === "number" && Number.isFinite(s.maxMb) ? ` Serwer przyjmuje pliki do ${s.maxMb} MB.` : "";
        return {
          pole: "ogolny",
          tekst: `Ten plik jest za duży, żeby go przeanalizować.${limit} Podziel nagranie na części albo zapisz je w mniejszym formacie, np. MP3.`,
        };
      }
      const dostawca = napis(s.dostawca) ?? "wybranego dostawcy";
      const dopuszczalne = typeof s.maxMb === "number" ? `przyjmuje pliki do ${s.maxMb} MB` : "nie przyjmuje tak dużych plików";
      return {
        pole: "ogolny",
        tekst: `Model transkrypcji ${dostawca} ${dopuszczalne}, a to nagranie jest większe. Duże nagrania mamy sprawdzone tylko z ElevenLabs - wybierz go w oknie Zmień modele albo podziel nagranie na części.`,
      };
    }
    case "brak_miejsca": {
      const wolne = typeof s.wolneBajtow === "number" ? formatujRozmiar(s.wolneBajtow) : null;
      const potrzebne = typeof s.potrzebneBajtow === "number" ? formatujRozmiar(s.potrzebneBajtow) : null;
      const liczby = wolne && potrzebne ? ` (potrzeba ${potrzebne}, wolne jest ${wolne})` : "";
      return {
        pole: "ogolny",
        tekst: `Na serwerze nie ma teraz miejsca na to nagranie${liczby}. Spróbuj później albo daj nam znać - dysk trzeba zwolnić.`,
      };
    }
    case "za_duzo_wgrywan":
      return {
        pole: "ogolny",
        tekst: "Serwer przyjmuje już najwięcej nagrań naraz. Poczekaj, aż któreś się skończy, i spróbuj ponownie.",
      };
    case "niekompletny_plik": {
      const dostalismy = typeof s.receivedBytes === "number" ? formatujRozmiar(s.receivedBytes) : null;
      const calosc = typeof s.sizeBytes === "number" ? formatujRozmiar(s.sizeBytes) : null;
      const liczby = dostalismy && calosc ? ` (${dostalismy} z ${calosc})` : "";
      return { pole: "ogolny", tekst: `Serwer dostał tylko część nagrania${liczby}. Wyślij nagranie jeszcze raz.` };
    }
    case "wgrywanie_juz_przetworzone_albo_przerwane":
      return {
        pole: "ogolny",
        tekst: napis(s.komunikat) ?? "To wysyłanie zostało już przetworzone albo przerwane po stronie serwera. Wyślij nagranie jeszcze raz.",
      };
    case "porcja_w_toku":
      return {
        pole: "ogolny",
        tekst: "Serwer jeszcze przetwarza poprzednią część nagrania. Spróbuj ponownie za chwilę.",
      };
    case "nie_ma_takiego_wgrywania":
      return {
        pole: "ogolny",
        tekst: "Serwer nie pamięta już tego wysyłania (przerwa była zbyt długa). Wyślij nagranie jeszcze raz.",
      };
    case "zla_dlugosc_porcji":
    case "zly_numer_porcji":
    case "niezgodna_odpowiedz_serwera":
      return {
        pole: "ogolny",
        tekst: `Serwer odebrał część nagrania w uszkodzonej postaci (${err.code}). Wyślij nagranie jeszcze raz, a jeśli się powtórzy - daj nam znać.`,
      };
    case "brak_sciezki_audio":
      return { pole: "plik", tekst: "W tym pliku nie ma ścieżki audio. Wybierz nagranie z dźwiękiem." };
    case "nagrania_wylaczone_dla_konta":
      // Przy przycisku, nie przy strefie pliku: w widoku uzytkownika strefy nagrania nie ma.
      return { pole: "ogolny", tekst: KOMUNIKAT_NAGRANIA_WYLACZONE };
    case "tekst_za_dlugi":
      return {
        pole: tryb === "plik" ? "plik" : "tekst",
        tekst:
          napis(s.komunikat) ??
          `Tekst jest za długi. Najdłuższy dozwolony tekst ma ${formatujLiczbe(liczba(s.maxZnakow, MAX_ZNAKOW_TEKSTU))} znaków. Podziel rozmowę na części.`,
      };
    case "tekst_pusty":
      return {
        pole: tryb === "plik" ? "plik" : "tekst",
        tekst: napis(s.komunikat) ?? KOMUNIKATY_BLEDOW_PARSERA.TEKST_PUSTY,
      };
    case "tekst_zle_kodowanie":
      return {
        pole: tryb === "plik" ? "plik" : "tekst",
        tekst: napis(s.komunikat) ?? KOMUNIKATY_BLEDOW_PARSERA.ZLE_KODOWANIE,
      };
    case "brak_zgody":
      return { pole: "zgoda", tekst: KOMUNIKAT_BRAKU_ZGODY };
    case "opis_wymagany":
      return { pole: "opis", tekst: uzytkownik ? KOMUNIKAT_BRAKU_OPISU_UZYTKOWNIKA : KOMUNIKAT_BRAKU_OPISU };
    case "opis_za_dlugi":
      return {
        pole: "opis",
        tekst: `Opis jest za długi - limit to ${liczba(s.maxZnakow, MAX_ZNAKOW_OPISU)} znaków. Skróć go.`,
      };
    case "zle_pole_opisu":
      return { pole: "opis", tekst: "Nie udało się odczytać opisu rozmowy. Wpisz go jeszcze raz." };
    case "brak_soczewek":
    case "zle_pole_soczewek":
      return { pole: "soczewki", tekst: KOMUNIKAT_BRAKU_PARAMETROW };
    case "nieznana_soczewka":
    case "soczewka_wylaczona":
      return {
        pole: "soczewki",
        tekst: "Jeden z wybranych parametrów nie jest już dostępny. Odświeżyliśmy listę - zaznacz parametry jeszcze raz.",
        odswiezParametry: true,
      };
    case "model_niedostepny":
      return {
        pole: "ogolny",
        tekst:
          "Nie możemy teraz ocenić tekstu, bo serwer nie ma połączenia z modelem oceny. Spróbuj ponownie później albo daj nam znać.",
      };
    case "nieznana_retencja":
    case "nieznany_dostawca_transkrypcji":
    case "nieznany_dostawca_ekspresji":
      return {
        pole: "ogolny",
        // Uzytkownik nie ma okna "Zmien modele": rada o nim bylaby slepa, a kod nic mu nie mowi.
        tekst: uzytkownik
          ? "Nie udało się przyjąć tego nagrania z obecnymi ustawieniami. Spróbuj ponownie później albo daj nam znać."
          : `Wybrane modele albo ustawienie nagrania nie są dostępne. Sprawdź je w oknie Zmień modele (${err.code}).`,
      };
    case "blad_wysylki":
      return { pole: "ogolny", tekst: "Nie udało się odebrać przesłanych danych. Spróbuj ponownie." };
    default:
      // Uzytkownik nie dostaje kodu w nawiasie: nic by mu nie powiedzial (nieznany kod, 502, 500, 403).
      return { pole: "ogolny", tekst: uzytkownik ? BLAD_OGOLNY_WGRANIA_UZYTKOWNIKA : `Nie udało się wgrać (${err.code}).` };
  }
}

// ---------------------------------------------------------------------------
// Nazwa przycisku i toast
// ---------------------------------------------------------------------------

export function etykietaPrzyciskuWysylki(tryb: TrybWejscia, zajety: boolean): string {
  if (tryb === "nagranie") return zajety ? "Wysyłam nagranie..." : "Analizuj nagranie";
  return zajety ? "Wysyłam tekst..." : "Analizuj tekst";
}

/** Przycisk wysylki w widoku uzytkownika: jedna czynnosc dla kazdego rodzaju materialu (CP-3). */
export function etykietaPrzyciskuOceny(zajety: boolean): string {
  return zajety ? "Wysyłam rozmowę..." : "Oceń rozmowę";
}

/** TO-1 w widoku uzytkownika: potwierdzenie bez slowa "analiza" ("ocena" to jego jezyk). */
export function potwierdzenieWgraniaUzytkownika(tryb: TrybWejscia, podpisany: boolean | undefined): string {
  if (tryb === "nagranie") return "Nagranie trafiło do oceny.";
  return podpisany === false ? "Tekst trafił do podziału na wypowiedzi." : "Tekst trafił do oceny.";
}

/** TO-1: krotkie potwierdzenie zdarzenia, zalezne od tego, co poszlo dalej. */
export function potwierdzenieWgrania(tryb: TrybWejscia, podpisany: boolean | undefined): string {
  if (tryb === "nagranie") return "Nagranie trafiło do analizy.";
  return podpisany === false ? "Tekst trafił do podziału na wypowiedzi." : "Tekst trafił do analizy.";
}
